(() => {
  const CATEGORY_STEPS = ['bay', 'screen', 'launch_monitor', 'projector', 'mat', 'accessory'];
  const REQUIRED_CATEGORIES = ['bay', 'launch_monitor', 'projector', 'mat'];

  class GraterSimBuilder {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-builder-config]').textContent);
      this.step = 0;
      this.room = {};
      this.selections = { accessory: [] };
      this.bind();
      this.updateUI();
    }

    bind() {
      this.root.querySelector('[data-next]').addEventListener('click', () => this.next());
      this.root.querySelector('[data-prev]').addEventListener('click', () => this.previous());
      this.root.querySelectorAll('[data-room]').forEach((input) => input.addEventListener('change', () => this.readRoom()));
      this.root.querySelectorAll('[data-select-product]').forEach((button) => button.addEventListener('click', () => this.toggleProduct(button.closest('[data-product-card]'))));
      this.root.querySelectorAll('[data-variant-select]').forEach((select) => select.addEventListener('change', () => this.variantChanged(select)));
      this.root.querySelector('[data-skip-screen]').addEventListener('change', () => {
        if (this.root.querySelector('[data-skip-screen]').checked) this.removeSelection('screen');
        this.renderSummary();
      });
      this.root.querySelector('[data-add-package]').addEventListener('click', () => this.addPackage());
      this.root.querySelector('[data-show-quote]').addEventListener('click', () => this.showQuote());
      this.root.querySelector('[data-sidebar-summary]').addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-category]');
        if (button) this.removeSelection(button.dataset.removeCategory, button.dataset.productId);
      });
    }

    readRoom() {
      this.root.querySelectorAll('[data-room]').forEach((input) => { this.room[input.dataset.room] = input.value; });
      this.room.width = Number(this.room.width || 0);
      this.room.depth = Number(this.room.depth || 0);
      this.room.height = Number(this.room.height || 0);
      this.renderRoomGuidance();
      this.evaluateAllCards();
      this.renderSummary();
    }

    renderRoomGuidance() {
      const box = this.root.querySelector('[data-room-guidance]');
      if (!this.room.width || !this.room.depth || !this.room.height) return;
      const usableWidth = Math.max(0, this.room.width - 1);
      const ratio = usableWidth / Math.max(this.room.height - 1, 1);
      let recommendedRatio = '4:3';
      if (ratio >= 1.65) recommendedRatio = '16:9';
      else if (ratio >= 1.5) recommendedRatio = '16:10';
      this.room.recommendedRatio = recommendedRatio;

      const hittingDistance = this.room.depth >= 18 ? 'comfortable for radar and camera systems' : this.room.depth >= 14 ? 'best suited to camera-based or compact radar systems' : 'tight; prioritize camera-based systems and a shallow enclosure';
      box.innerHTML = `<strong>Initial room guidance</strong><br>Recommended screen shape: ${recommendedRatio}. Your ${this.room.depth.toFixed(1)} ft depth is ${hittingDistance}. Always confirm full swing clearance at the tallest golfer’s hitting position.`;
      box.hidden = false;
    }

    validateRoom() {
      this.readRoom();
      const valid = this.room.width >= 6 && this.room.depth >= 8 && this.room.height >= 7 && this.room.handedness && this.room.use && this.room.budget;
      if (!valid) this.notice('Complete every room field with valid dimensions before continuing.', true);
      return valid;
    }

    evaluateAllCards() {
      this.root.querySelectorAll('[data-product-card]').forEach((card) => this.evaluateCard(card));
    }

    numberOrFallback(value, fallback = 0) {
      return value !== undefined && value !== null && value !== '' ? Number(value) : Number(fallback || 0);
    }

    textOrFallback(value, fallback = 'any') {
      return value && value.trim() ? value.trim().toLowerCase() : String(fallback || 'any').trim().toLowerCase();
    }

    listOrFallback(value, fallback = ['any']) {
      const values = String(value || '')
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean);
      if (values.length) return [...new Set(values)];
      if (Array.isArray(fallback)) return fallback.length ? fallback : ['any'];
      return [this.textOrFallback(fallback)];
    }

    booleanOrFallback(value, fallback = false) {
      if (value === undefined || value === null || value === '') return Boolean(fallback);
      return String(value).toLowerCase() === 'true';
    }

    productDefaults(card) {
      const fallbackAspects = [this.textOrFallback(card.dataset.aspectRatio)];
      const fallbackPlacements = [this.textOrFallback(card.dataset.placement)];
      return {
        minWidth: Number(card.dataset.minWidth || 0),
        minDepth: Number(card.dataset.minDepth || 0),
        minHeight: Number(card.dataset.minHeight || 0),
        maxWidth: Number(card.dataset.maxWidth || 0),
        productWidth: 0,
        productDepth: 0,
        productHeight: 0,
        aspectRatios: this.listOrFallback(card.dataset.aspectRatios, fallbackAspects),
        placements: this.listOrFallback(card.dataset.placements, fallbackPlacements),
        handedness: this.textOrFallback(card.dataset.handedness, 'both'),
        throwMin: 0,
        throwMax: 0,
        includesScreen: this.booleanOrFallback(card.dataset.includesScreen)
      };
    }

    variantMeta(element, card) {
      const fallback = this.productDefaults(card);
      return {
        minWidth: this.numberOrFallback(element.dataset.minWidth, fallback.minWidth),
        minDepth: this.numberOrFallback(element.dataset.minDepth, fallback.minDepth),
        minHeight: this.numberOrFallback(element.dataset.minHeight, fallback.minHeight),
        maxWidth: this.numberOrFallback(element.dataset.maxWidth, fallback.maxWidth),
        productWidth: this.numberOrFallback(element.dataset.productWidth, fallback.productWidth),
        productDepth: this.numberOrFallback(element.dataset.productDepth, fallback.productDepth),
        productHeight: this.numberOrFallback(element.dataset.productHeight, fallback.productHeight),
        aspectRatios: this.listOrFallback(element.dataset.aspectRatios, fallback.aspectRatios),
        placements: this.listOrFallback(element.dataset.placements, fallback.placements),
        handedness: this.textOrFallback(element.dataset.handedness, fallback.handedness),
        throwMin: this.numberOrFallback(element.dataset.throwMin, fallback.throwMin),
        throwMax: this.numberOrFallback(element.dataset.throwMax, fallback.throwMax),
        includesScreen: this.booleanOrFallback(element.dataset.includesScreen, fallback.includesScreen)
      };
    }

    compatibilityFor(card, meta) {
      const reasons = [];
      const warnings = [];
      const notes = [];

      if (meta.minWidth && this.room.width && this.room.width < meta.minWidth) reasons.push(`needs at least ${meta.minWidth} ft room width`);
      if (meta.minDepth && this.room.depth && this.room.depth < meta.minDepth) reasons.push(`needs at least ${meta.minDepth} ft room depth`);
      if (meta.minHeight && this.room.height && this.room.height < meta.minHeight) reasons.push(`needs at least ${meta.minHeight} ft ceiling height`);
      if (meta.maxWidth && this.room.width && this.room.width > meta.maxWidth) warnings.push(`optimized for rooms up to ${meta.maxWidth} ft wide`);
      if (this.room.handedness === 'both' && meta.handedness === 'single') warnings.push('may need repositioning for right- and left-handed golfers');
      const usablePlacements = meta.placements.filter((value) => value !== 'any');
      if (usablePlacements.length === 1 && usablePlacements[0] === 'radar' && this.room.depth && this.room.depth < 16 && !meta.minDepth) {
        reasons.push('radar systems generally need more ball-flight depth');
      } else if (usablePlacements.includes('radar') && this.room.depth && this.room.depth < 16 && !meta.minDepth) {
        warnings.push('radar placement needs more depth; use another supported placement mode');
      }
      if (usablePlacements.length === 1 && usablePlacements[0] === 'standard' && this.room.depth && this.room.depth < 16) {
        warnings.push('standard throw may create shadows in this depth');
      }

      const selectedBay = this.selections.bay;
      const selectedBayRatios = selectedBay?.variant?.meta?.aspectRatios || [];
      const selectedScreen = this.selections.screen;
      const selectedScreenRatios = selectedScreen?.variant?.meta?.aspectRatios || [];
      const targetRatio = selectedScreenRatios.find((ratio) => ratio !== 'any') || selectedBayRatios.find((ratio) => ratio !== 'any') || this.room.recommendedRatio;
      const supportsAnyRatio = meta.aspectRatios.includes('any');
      if (!supportsAnyRatio && targetRatio && card.dataset.category === 'projector' && !meta.aspectRatios.includes(targetRatio)) {
        warnings.push(`native ratio differs from the selected/recommended ${targetRatio} screen`);
      }

      if (card.dataset.category === 'screen' && selectedBay) {
        const screenRatios = meta.aspectRatios.filter((ratio) => ratio !== 'any');
        const bayRatios = selectedBayRatios.filter((ratio) => ratio !== 'any');
        if (screenRatios.length && bayRatios.length && !screenRatios.some((ratio) => bayRatios.includes(ratio))) {
          reasons.push(`screen ratio does not match the selected bay (${bayRatios.join(', ')})`);
        }
        const bayMeta = selectedBay.variant.meta;
        if (meta.productWidth && bayMeta.productWidth && meta.productWidth > bayMeta.productWidth) warnings.push(`screen width exceeds the bay's configured width`);
        if (meta.productHeight && bayMeta.productHeight && meta.productHeight > bayMeta.productHeight) warnings.push(`screen height exceeds the bay's configured height`);
      }

      if (meta.aspectRatios.filter((value) => value !== 'any').length > 1) {
        notes.push(`supports ${meta.aspectRatios.filter((value) => value !== 'any').join(', ')} aspect ratios`);
      }
      if (usablePlacements.length > 1) notes.push(`supports ${usablePlacements.join(', ')} configurations`);

      if (card.dataset.category === 'projector' && meta.throwMin > 0 && meta.throwMax > 0) {
        const screenWidth = selectedScreen?.variant?.meta?.productWidth || selectedBay?.variant?.meta?.productWidth || Math.max((this.room.width || 0) - 1, 0);
        if (screenWidth > 0) {
          const mountMin = screenWidth * meta.throwMin;
          const mountMax = screenWidth * meta.throwMax;
          notes.push(`estimated lens distance: ${mountMin.toFixed(1)}–${mountMax.toFixed(1)} ft for a ${screenWidth.toFixed(1)} ft image width`);
          if (this.room.depth && mountMin > Math.max(this.room.depth - 1, 0)) {
            reasons.push(`minimum calculated projector distance is ${mountMin.toFixed(1)} ft`);
          } else if (this.room.depth && mountMax > Math.max(this.room.depth - 1, 0)) {
            warnings.push(`the far end of the throw range exceeds the usable room depth`);
          }
        }
      }

      return { reasons, warnings, notes };
    }

    refreshVariantOptions(card) {
      const control = card.querySelector('[data-variant-select]');
      if (control.tagName !== 'SELECT') return;

      Array.from(control.options).forEach((option) => {
        const fit = this.compatibilityFor(card, this.variantMeta(option, card));
        option.disabled = option.dataset.available !== 'true' || fit.reasons.length > 0;
        if (!option.dataset.originalLabel) option.dataset.originalLabel = option.textContent;
        const reason = fit.reasons[0];
        option.textContent = reason ? `${option.dataset.originalLabel} — ${reason}` : option.dataset.originalLabel;
      });

      if (control.options[control.selectedIndex]?.disabled) {
        const fallback = Array.from(control.options).find((option) => !option.disabled);
        if (fallback) control.value = fallback.value;
      }
    }

    evaluateCard(card) {
      this.refreshVariantOptions(card);
      const variant = this.selectedVariant(card);
      const fit = this.compatibilityFor(card, variant.meta);
      const reasons = fit.reasons;
      const warnings = fit.warnings;
      const notes = fit.notes;

      const badge = card.querySelector('[data-fit-badge]');
      const message = card.querySelector('[data-fit-message]');
      card.classList.toggle('is-incompatible', reasons.length > 0);
      card.dataset.compatible = String(reasons.length === 0);
      if (reasons.length) {
        badge.textContent = 'Does not fit'; badge.className = 'gg-product-card__badge is-incompatible';
        message.textContent = `Not recommended: ${reasons.join('; ')}.`;
      } else if (warnings.length) {
        badge.textContent = 'Review fit'; badge.className = 'gg-product-card__badge is-warning';
        message.textContent = `Review: ${warnings.concat(notes).join('; ')}.`;
      } else {
        badge.textContent = 'Room compatible'; badge.className = 'gg-product-card__badge is-compatible';
        message.textContent = notes.length ? `Compatible. ${notes.join('; ')}.` : 'Compatible with the room information provided.';
      }
      card.querySelector('[data-card-price]').textContent = this.formatMoney(variant.price);
      card.querySelector('[data-select-product]').disabled = reasons.length > 0 || !this.variantAvailable(card);
      this.syncSelectedCard(card);
      return fit;
    }

    variantAvailable(card) {
      const select = card.querySelector('[data-variant-select]');
      if (select.tagName === 'SELECT') {
        const option = select.options[select.selectedIndex];
        return option?.dataset.available === 'true' && !option.disabled;
      }
      return select.dataset.available === 'true';
    }

    selectedVariant(card) {
      const select = card.querySelector('[data-variant-select]');
      if (select.tagName === 'SELECT') {
        const option = select.options[select.selectedIndex];
        return {
          id: option.value,
          price: Number(option.dataset.price),
          title: option.dataset.title || (option.dataset.originalLabel || option.textContent).trim(),
          available: option.dataset.available === 'true' && !option.disabled,
          meta: this.variantMeta(option, card)
        };
      }
      return {
        id: select.value,
        price: Number(select.dataset.price),
        title: select.dataset.title || 'Default',
        available: select.dataset.available === 'true',
        meta: this.variantMeta(select, card)
      };
    }

    variantChanged(select) {
      const card = select.closest('[data-product-card]');
      const variant = this.selectedVariant(card);
      card.querySelector('[data-card-price]').textContent = this.formatMoney(variant.price);
      this.evaluateCard(card);
      if (card.dataset.category === 'bay') {
        this.syncScreenRequirement();
        this.evaluateAllCards();
      }
      if (card.dataset.category === 'screen') this.evaluateAllCards();
      this.renderSummary();
    }

    syncSelectedCard(card) {
      if (!card.classList.contains('is-selected')) return;
      const category = card.dataset.category;
      if (category === 'accessory') {
        const item = this.selections.accessory.find((entry) => entry.productId === card.dataset.productId);
        if (item) Object.assign(item, this.cardData(card));
      } else {
        this.selections[category] = this.cardData(card);
      }
    }

    cardData(card) {
      const variant = this.selectedVariant(card);
      return { category: card.dataset.category, productId: card.dataset.productId, title: card.querySelector('h3').textContent.trim(), variantId: variant.id, variantTitle: variant.title, price: variant.price, variant, card };
    }

    toggleProduct(card) {
      if (card.dataset.compatible === 'false') return this.notice('That product does not meet the room requirements you entered.', true);
      const category = card.dataset.category;
      const data = this.cardData(card);
      if (category === 'accessory') {
        const index = this.selections.accessory.findIndex((item) => item.productId === data.productId);
        if (index >= 0) { this.selections.accessory.splice(index, 1); card.classList.remove('is-selected'); }
        else { this.selections.accessory.push(data); card.classList.add('is-selected'); }
      } else {
        this.root.querySelectorAll(`[data-product-card][data-category="${category}"]`).forEach((item) => item.classList.remove('is-selected'));
        this.selections[category] = data;
        card.classList.add('is-selected');
      }
      this.updateSelectLabels(category);
      if (category === 'bay') {
        this.syncScreenRequirement();
        this.evaluateAllCards();
      }
      if (category === 'screen') {
        const skip = this.root.querySelector('[data-skip-screen]');
        if (!skip.disabled) skip.checked = false;
        this.evaluateAllCards();
      }
      this.renderSummary();
      this.notice('');
    }

    updateSelectLabels(category) {
      this.root.querySelectorAll(`[data-product-card][data-category="${category}"]`).forEach((card) => {
        const selected = card.classList.contains('is-selected');
        card.querySelector('[data-select-product]').textContent = selected ? 'Selected' : category === 'accessory' ? 'Add accessory' : 'Select';
      });
    }

    syncScreenRequirement() {
      const skip = this.root.querySelector('[data-skip-screen]');
      const guidance = this.root.querySelector('[data-screen-guidance]');
      const bay = this.selections.bay;
      const included = Boolean(bay?.variant?.meta?.includesScreen);
      if (included) {
        skip.checked = true;
        skip.disabled = true;
        skip.dataset.auto = 'true';
        if (this.selections.screen) this.removeSelection('screen');
        guidance.textContent = `${bay.title} is configured as including an impact screen, so no separate screen will be added.`;
        guidance.hidden = false;
      } else {
        if (skip.dataset.auto === 'true') skip.checked = false;
        skip.disabled = false;
        delete skip.dataset.auto;
        guidance.textContent = bay ? 'Select a compatible screen below, or check the box if this bay includes a screen that is not configured in the builder.' : '';
        guidance.hidden = !bay;
      }
    }

    next() {
      if (this.step === 0 && !this.validateRoom()) return;
      if (this.step >= 1 && this.step <= 5) {
        const category = CATEGORY_STEPS[this.step - 1];
        if (category === 'screen' && this.root.querySelector('[data-skip-screen]').checked) {
          this.step += 1; this.updateUI(); this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); return;
        }
        if (!this.selections[category]) return this.notice(`Choose a ${this.config.labels[category].toLowerCase()} before continuing.`, true);
        const selected = this.selections[category];
        if (selected.card.dataset.compatible !== 'true' || !this.selectedVariant(selected.card).available) {
          return this.notice(`The selected ${this.config.labels[category].toLowerCase()} configuration is not compatible or available. Choose another configuration.`, true);
        }
      }
      if (this.step === 6) {
        if (!this.selections.accessory.length && !this.root.querySelector('[data-skip-accessories]').checked) return this.notice('Select accessories or check “Continue without accessories.”', true);
        const invalidAccessory = this.selections.accessory.find((item) => item.card.dataset.compatible !== 'true' || !this.selectedVariant(item.card).available);
        if (invalidAccessory) return this.notice(`Remove or change the incompatible accessory: ${invalidAccessory.title}.`, true);
      }
      if (this.step < 7) { this.step += 1; this.updateUI(); this.root.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }

    previous() { if (this.step > 0) { this.step -= 1; this.updateUI(); } }

    updateUI() {
      this.root.querySelectorAll('[data-step]').forEach((panel) => panel.classList.toggle('is-active', Number(panel.dataset.step) === this.step));
      this.root.querySelectorAll('[data-progress-step]').forEach((item) => {
        const index = Number(item.dataset.progressStep);
        item.classList.toggle('is-active', index === this.step);
        item.classList.toggle('is-complete', index < this.step);
      });
      this.root.querySelector('[data-progress-bar]').style.width = `${(this.step / 7) * 100}%`;
      this.root.querySelector('[data-prev]').hidden = this.step === 0;
      const next = this.root.querySelector('[data-next]');
      next.hidden = this.step === 7;
      const labels = ['See compatible bays', 'Choose impact screen', 'Choose launch monitor', 'Choose projector', 'Choose hitting mat', 'Choose accessories', 'Review package'];
      next.textContent = labels[this.step] || 'Continue';
      this.evaluateAllCards();
      if (this.step === 7) this.renderFinal();
      this.notice('');
    }

    selectedItems() {
      return CATEGORY_STEPS.flatMap((category) => category === 'accessory' ? this.selections.accessory : (this.selections[category] ? [this.selections[category]] : []));
    }

    renderSummary() {
      const items = this.selectedItems();
      const container = this.root.querySelector('[data-sidebar-summary]');
      const includedScreen = !this.selections.screen && this.root.querySelector('[data-skip-screen]').checked;
      const rows = items.map((item) => `<div class="gg-builder__summary-row"><div><span>${this.config.labels[item.category]}</span><strong>${this.escape(item.title)}</strong></div><b>${this.formatMoney(item.price)}</b><button type="button" data-remove-category="${item.category}" data-product-id="${item.productId}" aria-label="Remove ${this.escape(item.title)}">Remove</button></div>`);
      if (includedScreen) rows.splice(1, 0, '<div class="gg-builder__summary-row"><div><span>Impact screen</span><strong>Included with bay / no separate screen</strong></div><b>Included</b></div>');
      container.innerHTML = rows.length ? rows.join('') : '<p>No products selected yet.</p>';
      const total = items.reduce((sum, item) => sum + item.price, 0);
      this.root.querySelector('[data-total]').textContent = this.formatMoney(total);
    }

    renderFinal() {
      this.renderSummary();
      this.root.querySelector('[data-final-summary]').innerHTML = this.root.querySelector('[data-sidebar-summary]').innerHTML;
      const warnings = this.selectedItems().flatMap((item) => {
        const fit = this.evaluateCard(item.card);
        return fit.reasons.concat(fit.warnings, fit.notes).map((text) => `${item.title}: ${text}`);
      });
      this.root.querySelector('[data-final-compatibility]').innerHTML = warnings.length ? `<ul>${warnings.map((item) => `<li>${this.escape(item)}</li>`).join('')}</ul>` : '<p>All selected products match the compatibility information configured by Grater Golf.</p>';
    }

    removeSelection(category, productId) {
      if (category === 'accessory') this.selections.accessory = this.selections.accessory.filter((item) => item.productId !== productId);
      else delete this.selections[category];
      this.root.querySelectorAll(`[data-product-card][data-product-id="${productId}"]`).forEach((card) => card.classList.remove('is-selected'));
      this.updateSelectLabels(category); this.renderSummary(); if (category === 'bay') this.syncScreenRequirement(); if (this.step === 7) this.renderFinal();
    }

    buildProperties() {
      return {
        '_Grater Golf Build': this.buildId(),
        'Room dimensions': `${this.room.width} ft W × ${this.room.depth} ft D × ${this.room.height} ft H`,
        'Golfer handedness': this.room.handedness,
        'Primary use': this.room.use,
        'Impact screen': this.selections.screen ? this.selections.screen.title : 'Included with bay / no separate screen',
        'Customer notes': this.root.querySelector('[data-customer-notes]').value || 'None'
      };
    }

    buildId() {
      if (!this._buildId) this._buildId = `GG-${Date.now().toString(36).toUpperCase()}`;
      return this._buildId;
    }

    async addPackage() {
      const button = this.root.querySelector('[data-add-package]');
      const items = this.selectedItems();
      const missing = REQUIRED_CATEGORIES.find((category) => !this.selections[category]);
      const screenMissing = !this.selections.screen && !this.root.querySelector('[data-skip-screen]').checked;
      if (missing || screenMissing) return this.notice('Your package is missing a required component. Go back and complete each step.', true);
      const invalid = items.find((item) => item.card.dataset.compatible !== 'true' || !this.selectedVariant(item.card).available);
      if (invalid) return this.notice(`${invalid.title} is no longer compatible or available. Update that selection before adding the package.`, true);
      button.disabled = true; button.textContent = 'Adding package…';
      try {
        const response = await fetch(this.root.dataset.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }, body: JSON.stringify({ items: items.map((item) => ({ id: Number(item.variantId), quantity: 1, properties: this.buildProperties() })) }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || 'The package could not be added.');
        window.location.href = this.root.dataset.cartUrl;
      } catch (error) {
        this.notice(error.message || 'The package could not be added. Please request a quote for assistance.', true);
        button.disabled = false; button.textContent = 'Add package to cart';
      }
    }

    showQuote() {
      const panel = this.root.querySelector('[data-quote-panel]');
      panel.hidden = false;
      this.root.querySelector('[data-quote-summary]').value = this.quoteText();
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    quoteText() {
      const items = this.selectedItems();
      const total = items.reduce((sum, item) => sum + item.price, 0);
      const screenLine = !this.selections.screen && this.root.querySelector('[data-skip-screen]').checked ? ['- Impact screen: Included with bay / no separate screen'] : [];
      return [`Build ID: ${this.buildId()}`, `Room: ${this.room.width} ft W x ${this.room.depth} ft D x ${this.room.height} ft H`, `Handedness: ${this.room.handedness}`, `Use: ${this.room.use}`, `Budget: ${this.room.budget}`, '', 'Selections:', ...items.map((item) => `- ${this.config.labels[item.category]}: ${item.title} / ${item.variantTitle} / ${this.formatMoney(item.price)}`), ...screenLine, '', `Estimated total: ${this.formatMoney(total)}`, `Notes: ${this.root.querySelector('[data-customer-notes]').value || 'None'}`].join('\n');
    }

    formatMoney(cents) { return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.config.currency }).format(cents / 100); }
    escape(value) { const div = document.createElement('div'); div.textContent = value; return div.innerHTML; }
    notice(message, error = false) { const box = this.root.querySelector('[data-builder-notice]'); box.textContent = message; box.hidden = !message; box.classList.toggle('is-error', error); }
  }

  const init = (scope = document) => scope.querySelectorAll('.gg-builder').forEach((root) => { if (!root.dataset.initialized) { root.dataset.initialized = 'true'; new GraterSimBuilder(root); } });
  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
